import { NgModule } from "@angular/core";
import {
  PreloadAllModules,
  RouterModule,
  Routes,
  UrlSegment,
} from "@angular/router";
import { AuthGuard } from "./guards/auth.guard";

const routes: Routes = [
  {
    path: "menus/:id",
    loadChildren: () =>
      import("./pages/public/installation-menu/installation-menu.module").then(
        (m) => m.InstallationMenuPageModule
      ),
  },
  {
    path: "parking/:installation/:terminal/:relay/:ver",
    loadChildren: () =>
      import(
        "./pages/public/installation-parking/installation-parking.module"
      ).then((m) => m.InstallationParkingPageModule),
  },
  {
    path: "privacy-policy",
    loadChildren: () =>
      import("./pages/public/privacy-policy/privacy-policy.module").then(
        (m) => m.PrivacyPolicyPageModule
      ),
  },
  {
    path: "account",
    data: {
      roles: ["admin"],
    },
    loadChildren: () =>
      import("./pages/members/account/account.module").then(
        (m) => m.AccountPageModule
      ),
  },
  {
    path: "dashboard",
    canActivate: [AuthGuard],
    data: {
      roles: ["admin", "admin_support", "seller"],
    },
    loadChildren: () =>
      import("./pages/admin/dashboard/dashboard.module").then(
        (m) => m.DashboardPageModule
      ),
  },
  {
    path: "dashboard/charts",
    data: {
      roles: ["admin", "admin_support"],
    },
    canActivate: [AuthGuard],
    loadChildren: () =>
      import("./pages/admin/dashboard-charts/dashboard-charts.module").then(
        (m) => m.DashboardChartsPageModule
      ),
  },
  {
    path: "home",
    canActivate: [AuthGuard],
    data: {
      roles: [
        "manager",
        "contractor",
        "customer_support",
        "technical_support",
        "admin_support",
        "admin",
      ],
    },
    loadChildren: () =>
      import("./pages/members/home/home.module").then((m) => m.HomePageModule),
  },
  {
    path: "installations",
    canActivate: [AuthGuard],
    data: {
      roles: ["admin", "admin_support"],
    },
    loadChildren: () =>
      import("./pages/admin/installations/installations.module").then(
        (m) => m.InstallationsPageModule
      ),
  },
  {
    path: "dss",
    canActivate: [AuthGuard],
    data: {
      roles: ["admin", "technical_support", "admin_support"],
    },
    loadChildren: () =>
      import("./pages/admin/dss/dss-devices/dss-devices.module").then(
        (m) => m.DssDevicesPageModule
      ),
  },

  {
    path: "hik",
    canActivate: [AuthGuard],
    data: {
      roles: ["admin", "technical_support", "admin_support"],
    },
    loadChildren: () =>
      import("./pages/admin/hik/hik-devices/hik-devices.module").then(
        (m) => m.HikDevicesPageModule
      ),
  },
  {
    path: "devices",
    data: {
      status: "all",
    },
    loadChildren: () =>
      import("./pages/admin/devices/devices.module").then(
        (m) => m.DevicesPageModule
      ),
  },
  {
    path: "do",
    data: {
      status: "offline",
    },
    loadChildren: () =>
      import("./pages/admin/devices/devices.module").then(
        (m) => m.DevicesPageModule
      ),
  },
  {
    path: "map",
    canActivate: [AuthGuard],
    data: {
      roles: ["admin", "admin_support"],
    },
    loadChildren: () =>
      import("./pages/admin/map/map.module").then((m) => m.MapPageModule),
  },
  {
    path: "patch-notes/new",
    canActivate: [AuthGuard],
    data: {
      roles: ["admin", "admin_support"],
    },
    loadChildren: () =>
      import("./pages/admin/patch-notes/patch-notes.module").then(
        (m) => m.PatchNotesPageModule
      ),
  },
  {
    path: "patch-notes/:app/edit/:version",
    canActivate: [AuthGuard],
    data: {
      roles: ["admin", "admin_support"],
    },
    loadChildren: () =>
      import("./pages/admin/patch-notes-detail/patch-notes-detail.module").then(
        (m) => m.PatchNotesDetailPageModule
      ),
  },
  {
    path: "reports/queries",
    data: {
      roles: ["admin", "admin_support"],
    },
    canActivate: [AuthGuard],
    loadChildren: () =>
      import("./pages/admin/report-query/report-query.module").then(
        (m) => m.ReportQueryPageModule
      ),
  },
  {
    path: "reports/agents",
    data: {
      roles: ["admin", "admin_support"],
    },
    canActivate: [AuthGuard],
    loadChildren: () =>
      import("./pages/admin/support-report/support-report.module").then(
        (m) => m.SupportReportPageModule
      ),
  },
  {
    path: "reports/access",
    data: {
      roles: ["admin", "admin_support", "customer_support"],
    },
    canActivate: [AuthGuard],
    loadChildren: () =>
      import("./pages/admin/report-access/report-access.module").then(
        (m) => m.ReportAccessPageModule
      ),
  },
  {
    path: "reports/user_access",
    data: {
      roles: ["admin", "admin_support", "customer_support"],
    },
    canActivate: [AuthGuard],
    loadChildren: () =>
      import("./pages/admin/report-access-user/report-access-user.module").then(
        (m) => m.ReportAccessUserPageModule
      ),
  },
  {
    path: "reports/subscriptions",
    data: {
      roles: ["admin", "admin_support", "customer_support"],
    },
    canActivate: [AuthGuard],
    loadChildren: () =>
      import(
        "./pages/admin/report-subscriptions/report-subscriptions.module"
      ).then((m) => m.ReportSubscriptionsPageModule),
  },
  {
    path: "reports/homeowners",
    data: {
      roles: ["admin", "admin_support"],
    },
    canActivate: [AuthGuard],
    loadChildren: () =>
      import("./pages/admin/report-residents/report-residents.module").then(
        (m) => m.ReportResidentsPageModule
      ),
  },
  {
    path: "reports/managers",
    data: {
      roles: ["admin", "admin_support"],
    },
    canActivate: [AuthGuard],
    loadChildren: () =>
      import("./pages/admin/report-managers/report-managers.module").then(
        (m) => m.ReportManagersPageModule
      ),
  },
  {
    path: "reports/sales",
    data: {
      roles: ["admin", "admin_support"],
    },
    canActivate: [AuthGuard],
    loadChildren: () =>
      import(
        "./pages/admin/report-subscriptions-sales/report-subscriptions-sales.module"
      ).then((m) => m.ReportSubscriptionsSalesPageModule),
  },
  {
    path: "reports/residents",
    data: {
      roles: ["admin", "admin_support", "customer_support"],
    },
    canActivate: [AuthGuard],
    loadChildren: () =>
      import(
        "./pages/admin/report-installation-residents/report-installation-residents.module"
      ).then((m) => m.ReportInstallationResidentsPageModule),
  },
  {
    path: "support",
    data: {
      roles: ["admin", "admin_support"],
    },
    loadChildren: () =>
      import("./pages/admin/support-inbox/support-inbox.module").then(
        (m) => m.SupportInboxPageModule
      ),
  },
  {
    path: "sos",
    canActivate: [AuthGuard],
    data: {
      roles: ["sos_support"],
    },
    loadChildren: () =>
      import("./pages/admin/sos/sos.module").then((m) => m.SosPageModule),
  },
  {
    path: "users",
    data: {
      roles: ["admin", "admin_support", "customer_support"],
    },
    canActivate: [AuthGuard],
    loadChildren: () =>
      import("./pages/admin/users/users.module").then((m) => m.UsersPageModule),
  },
  {
    path: "manager",
    canActivate: [AuthGuard],
    data: {
      roles: [
        "manager",
        "contractor",
        "admin",
        "admin_support",
        "customer_support",
        "technical_support",
      ],
    },
    loadChildren: () =>
      import("./pages/manager/manager/manager.module").then(
        (m) => m.ManagerPageModule
      ),
  },
  {
    path: "signup",
    loadChildren: () =>
      import("./pages/public/signup/signup.module").then(
        (m) => m.SignupPageModule
      ),
  },
  {
    path: "login",
    loadChildren: () =>
      import("./pages/public/login/login.module").then(
        (m) => m.LoginPageModule
      ),
  },
  {
    path: "acc",
    loadChildren: () =>
      import("./pages/public/acc/acc.module").then((m) => m.AccPageModule),
  },
  {
    path: "coupon/:id",
    loadChildren: () =>
      import("./pages/public/coupon/coupon.module").then(
        (m) => m.CouponPageModule
      ),
  },
  {
    path: "coupon/:id/:associate",
    loadChildren: () =>
      import("./pages/public/coupon/coupon.module").then(
        (m) => m.CouponPageModule
      ),
  },
  {
    path: "ticket",
    loadChildren: () =>
      import("./pages/public/coupon-ticket/coupon-ticket.module").then(
        (m) => m.CouponTicketPageModule
      ),
  },
  {
    path: "event/:id/:type",
    loadChildren: () =>
      import("./pages/public/event/event.module").then(
        (m) => m.EventPageModule
      ),
  },
  {
    path: "t/:id/:type",
    loadChildren: () =>
      import("./pages/public/event/event.module").then(
        (m) => m.EventPageModule
      ),
  },
  {
    path: "event/:id",
    loadChildren: () =>
      import("./pages/public/event/event.module").then(
        (m) => m.EventPageModule
      ),
  },
  {
    path: "t/:id",
    loadChildren: () =>
      import("./pages/public/event/event.module").then(
        (m) => m.EventPageModule
      ),
  },
  {
    path: "event/",
    loadChildren: () =>
      import("./pages/public/not-found/not-found.module").then(
        (m) => m.NotFoundPageModule
      ),
  },
  {
    path: "t/",
    loadChildren: () =>
      import("./pages/public/not-found/not-found.module").then(
        (m) => m.NotFoundPageModule
      ),
  },
  {
    path: "event-detail",
    loadChildren: () =>
      import("./pages/public/event-detail/event-detail.module").then(
        (m) => m.EventDetailPageModule
      ),
  },
  {
    path: "t-d/:id",
    loadChildren: () =>
      import("./pages/public/event-detail/event-detail.module").then(
        (m) => m.EventDetailPageModule
      ),
  },
  {
    path: "m-v/:id",
    loadChildren: () =>
      import("./pages/public/template/template.module").then(
        (m) => m.TemplatePageModule
      ),
  },
  {
    path: "event-detail/opening",
    loadChildren: () =>
      import("./pages/public/event-opening/event-opening.module").then(
        (m) => m.EventOpeningPageModule
      ),
  },
  {
    path: "event-detail/opening/:isParking",
    loadChildren: () =>
      import("./pages/public/event-opening/event-opening.module").then(
        (m) => m.EventOpeningPageModule
      ),
  },
  {
    path: "payment",
    loadChildren: () =>
      import("./pages/public/payment/payment.module").then(
        (m) => m.PaymentPageModule
      ),
  },
  {
    path: "payment/:type/:id/:currency",
    loadChildren: () =>
      import("./pages/public/payment/payment.module").then(
        (m) => m.PaymentPageModule
      ),
  },
  {
    path: "code",
    loadChildren: () =>
      import("./pages/public/code/code.module").then((m) => m.CodePageModule),
  },
  {
    path: "download",
    loadChildren: () =>
      import("./pages/public/app-download/app-download.module").then(
        (m) => m.AppDownloadPageModule
      ),
  },
  {
    path: "credential",
    loadChildren: () =>
      import("./pages/public/credential/credential.module").then(
        (m) => m.CredentialPageModule
      ),
  },
  {
    path: "invitacion",
    loadChildren: () =>
      import("./pages/public/event-special/event-special.module").then(
        (m) => m.EventSpecialPageModule
      ),
  },
  {
    path: "404",
    loadChildren: () =>
      import("./pages/public/not-found/not-found.module").then(
        (m) => m.NotFoundPageModule
      ),
  },
  {
    path: "forbidden",
    loadChildren: () =>
      import("./pages/public/forbidden/forbidden.module").then(
        (m) => m.ForbiddenPageModule
      ),
  },
  {
    path: "",
    redirectTo: "login",
    pathMatch: "full",
  },
  {
    path: "**",
    loadChildren: () =>
      import("./pages/public/not-found/not-found.module").then(
        (m) => m.NotFoundPageModule
      ),
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, { preloadingStrategy: PreloadAllModules }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}

export function matcherFunction(url: UrlSegment[]) {
  if (url.length === 1) {
    const path = url[0].path;
    if (
      path.startsWith("today") ||
      path.startsWith("tomorrow") ||
      path.startsWith("expired")
    ) {
      return { consumed: url };
    }
  }
  return null;
}
